import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RealmSelect } from 'app/frontend/base/components'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z, ZodType } from 'zod'
import { transferCharacter } from 'app/frontend/base/api/character'
import { transferGuild } from 'app/frontend/base/api/guild'
import GenericFormError from 'app/frontend/base/components/GenericFormError'
import { awaitPendingJob, displayErrorToast } from 'app/frontend/base/helpers'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { Alert, AlertDescription } from '@/components/ui/alert'

const zodRealm = z
  .object({
    id: z.number(),
    name: z.string(),
    nameWithRegion: z.string(),
    localizedName: z.string(),
    region:
      z.literal('EU') || z.literal('US') || z.literal('KR') || z.literal('TW'),
    slug: z.string(),
  })
  .required() as ZodType<wowaudit.Realm>

type Props =
  | {
      scope: 'character'
      entity: wowaudit.CharacterReference
      setOpen: (open: boolean) => void
    }
  | {
      scope: 'guild'
      entity: wowaudit.GuildReference
      setOpen: (open: boolean) => void
    }

const TransferModal = (props: Props) => {
  const [submitting, setSubmitting] = useState(false)
  const queryClient = useQueryClient()
  const [error, setError] = useState<string>()
  const { t } = useTranslation()

  const nameSchema =
    props.scope === 'guild'
      ? z.string().min(2).max(24)
      : z.string().min(2).max(12).regex(/^\S*$/, 'Name must not contain spaces')

  const formSchema = z.object({ name: nameSchema, realm: zodRealm })

  const form = useForm<z.infer<typeof formSchema> & { genericError?: string }>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: props.entity.name,
      realm: props.entity.realm,
    },
  })

  const cannotTransfer =
    props.scope === 'character' &&
    !props.entity.achievementUid &&
    !props.entity.userId

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    setError(undefined)
    setSubmitting(true)

    const request =
      props.scope === 'guild'
        ? transferGuild(props.entity.id, values)
        : transferCharacter(props.entity.id, values)

    request
      .then(async (response) => {
        awaitPendingJob(response.job_id, async (result) => {
          if (result.error) {
            setError(t(result.error))
          } else {
            if (props.scope === 'guild') {
              await queryClient.invalidateQueries({ queryKey: ['guild'] })
            }
            await queryClient.invalidateQueries({ queryKey: ['pageInfo'] })

            toast.success(
              t(
                props.scope === 'guild'
                  ? 'profile.guild-transferred'
                  : 'profile.character-transferred',
              ),
            )
            props.setOpen(false)
          }

          setSubmitting(false)
        })
      })
      .catch((response) => {
        displayErrorToast(response, t)
        setSubmitting(false)
        setError(t('errors.fallback_description'))
      })
  }

  return (
    <Dialog open={true} onOpenChange={props.setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t(`transfer.${props.scope}.title`, { name: props.entity.name })}
          </DialogTitle>
          <DialogDescription>
            {t(`transfer.${props.scope}.description`)}
          </DialogDescription>
        </DialogHeader>

        {cannotTransfer && (
          <Alert variant='warning'>
            <AlertDescription>
              {t('transfer.character.cannot-transfer')}
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} id='transfer-form'>
            <div className='flex gap-2'>
              <FormItem>
                <FormLabel>{t('transfer.realm-label')}</FormLabel>
                <RealmSelect
                  className='flex'
                  disabled={cannotTransfer}
                  realm={form.watch('realm')}
                  setRealm={(realm) => {
                    if (realm) form.setValue('realm', realm)
                  }}
                  inModal={true}
                />
              </FormItem>

              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem className='grow'>
                    <FormLabel>{t('transfer.name-label')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t(
                          `transfer.${props.scope}.name-placeholder`,
                        )}
                        {...field}
                        disabled={cannotTransfer}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <GenericFormError customError={error} />

            {error && (
              <p className='text-sm text-muted-foreground mt-2'>
                <Trans
                  i18nKey='transfer.support-cta'
                  components={[
                    <a
                      key='discord'
                      href='https://discord.gg/86eUAFz'
                      target='_blank'
                      rel='noreferrer'
                      className='text-primary'
                    />,
                  ]}
                />
              </p>
            )}
          </form>
        </Form>

        <DialogFooter>
          <DialogClose asChild>
            <Button type='button' variant='secondary'>
              {t('shared.cancel')}
            </Button>
          </DialogClose>
          <Button
            type='submit'
            form='transfer-form'
            loading={submitting}
            disabled={cannotTransfer}
            spinner='blizzard'
          >
            {t('transfer.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default TransferModal
